import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { closeChat } from './toasts';
import { useAuth } from '../lib/auth';
import { ICONS } from './nav';
import { UserMenu } from './user-menu';
import { TestModeToggle, WorkspaceSwitcher } from './workspace';
import { useApi } from './kit';
import { ChatGlyph, chatKind, chatState, type ChatRowLike } from './chat-icons';
import { Mark } from './logo';

// The new experience's only navigation (as Bridge's): a narrow icon rail with the main places, the recent campaign
// threads (unread dot) and +, then the workspace (switch, test mode) and the account menu, where settings live.
// Everything else is reached from the chat.
// Phones get the tab bar instead (ui/tabbar.tsx), and on a thread page the chat with the cards in a sheet.

type ThreadRow = ChatRowLike & { id: string; subject_id: string | null; title: string; unread: number };


const WIDE_KEY = 'olv_rail_wide';
const loadWide = () => { try { return localStorage.getItem(WIDE_KEY) === '1'; } catch { return false; } };

/** The rail widens to show each place's name (owner request 2026-10-26); the choice is remembered per browser. */
function useWideRail() {
  const [wide, setWide] = useState(loadWide);
  useEffect(() => {
    document.documentElement.classList.toggle('rail-wide', wide);
    try { localStorage.setItem(WIDE_KEY, wide ? '1' : '0'); } catch { /* private mode */ }
  }, [wide]);
  useEffect(() => () => document.documentElement.classList.remove('rail-wide'), []);
  return [wide, () => setWide((w) => !w)] as const;
}

export function IconRail({ inboxBadge }: { inboxBadge: number; canIntegrate?: boolean }) {
  const [wide, toggleWide] = useWideRail();
  const threads = useApi<{ data: ThreadRow[] }>('/portal/threads', { refetchMs: 30_000 });
  const { pathname } = useLocation();
  const { staff, me } = useAuth();
  const nav = useNavigate();
  const [height, setHeight] = useState(() => (typeof window === 'undefined' ? 1000 : window.innerHeight));
  useEffect(() => { const f = () => setHeight(window.innerHeight); window.addEventListener('resize', f); return () => window.removeEventListener('resize', f); }, []);
  const close = async (t: ThreadRow, to: string) => {
    closeChat(api, t.id, t.title, async () => { await threads.refetch(); if (pathname === to) nav('/chats'); });
  };
  const items: Array<{ to: string; label: string; icon: string; end?: boolean; badge?: number; also?: string[] }> = [
    { to: '/', label: 'Home', icon: 'overview', end: true },
    { to: '/chats', label: 'Chats', icon: 'chats', badge: (threads.data?.data ?? []).filter((t) => t.unfinished).length, also: ['/chat'] },
    { to: '/inbox', label: 'Inbox', icon: 'work', badge: inboxBadge, also: ['/approvals', '/handoffs', '/replies'] },
    { to: '/work', label: 'All work', icon: 'queues', also: ['/campaigns'] },
    { to: '/accounts', label: 'Accounts', icon: 'customers', also: ['/import', '/actions', '/visitors'] },
    { to: '/context', label: 'Company & ICP', icon: 'setup', also: ['/icp', '/competitors'] },
    // the apps a workspace connects (CRM, ads, LinkedIn, calendar…): in plain sight, not only in the account menu (owner request 2026-10-23)
    { to: '/integrations', label: 'Integrations', icon: 'plug' },
  ];
  const on = (i: (typeof items)[number]) => (i.end ? pathname === i.to : pathname.startsWith(i.to)) || (i.also ?? []).some((p) => pathname === p || (pathname.startsWith(`${p}/`) && !/^\/campaigns\/[0-9a-f-]{36}/.test(pathname)));
  // recent chats and campaigns, newest first
  const open = (threads.data?.data ?? []).filter((t) => t.subject === 'task' || t.subject === 'campaign');
  // as many as fit above the workspace and account buttons (at 10 chats the "+" and "+3" went under them): the rest
  // of the rail, with "+N" and "+", takes about 648px, a chat 56px (measured live 2026-11-01: 7 chats at 1000px hid "+")
  const fit = Math.max(0, Math.min(7, Math.floor((height - 648) / 56)));
  const recent = open.slice(0, fit);
  // the rest are a click away (beyond 7 they could not be reached from the rail at all)
  const more = open.length - recent.length;

  return (
    <nav className="icon-rail" aria-label="Main">
      <div className="ir-top">
        <Link to="/" className="ir-brand" aria-label="Outlevo home">
          <Mark className="mark" />
        </Link>
        <button type="button" className="ir-expand" aria-expanded={wide} aria-label={wide ? 'Hide section names' : 'Show section names'} title={wide ? 'Hide section names' : 'Show section names'} onClick={toggleWide}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3.5" y="4.5" width="17" height="15" rx="3" /><path d="M9.5 4.5v15" /><path d={wide ? 'M15.5 10l-2 2 2 2' : 'M13.5 10l2 2-2 2'} /></svg>
        </button>
      </div>
      <ul>
        {items.map((i) => (
          <li key={i.to}>
            <NavLink to={i.to} end={i.end} title={i.label} aria-label={i.label} className={on(i) ? 'active' : ''} aria-current={on(i) ? 'page' : undefined}>
              {ICONS[i.icon]}<span className="ir-label">{i.label}</span>{i.badge ? <span className="ir-dot" aria-label={`${i.badge} waiting`} /> : null}
            </NavLink>
          </li>
        ))}
      </ul>
      {recent.length ? <div className="ir-sep" role="separator" /> : null}
      <ul aria-label="Recent chats and campaigns">
        {recent.map((t) => {
          const to = t.subject === 'task' ? `/chat/${t.subject_id}` : `/campaigns/${t.subject_id}`;
          return (
            <li key={t.id} className="ir-chat-item">
              <button type="button" className="ir-close" aria-label={`Close the chat “${t.title}”`} title="Close this chat" onClick={() => void close(t, to)}>×</button>
              <NavLink to={to} title={`${t.title}\n${chatKind(t)[0]} · ${chatState(t).label}`} aria-label={`${t.title}: ${chatKind(t)[0]}, ${chatState(t).label}${t.unread ? `, ${t.unread} unread` : ''}`}
                className={`ir-chat${pathname === to ? ' current' : ''}`}>
                <ChatGlyph c={t} /><span className="ir-label">{t.title}</span>
              </NavLink>
            </li>
          );
        })}
        {more > 0 ? <li><NavLink to="/chats" title={`${more} more chat${more === 1 ? '' : 's'}`} aria-label={`All chats: ${more} more`} className="ir-more" end><span aria-hidden="true">+{more}</span><span className="ir-label">All chats</span></NavLink></li> : null}
        <li><NavLink to="/campaigns/new" title="New campaign" aria-label="New campaign" className="ir-new" end><span aria-hidden="true">+</span><span className="ir-label">New campaign</span></NavLink></li>
      </ul>
      <div className="ir-foot">
        {staff ? <Link to="/ops" className="ir-admin" title="Admin panel" aria-label="Admin panel">{ICONS.compliance}</Link> : null}
        {me ? <WorkspaceButton name={me.org.name} sandbox={Boolean(me.org.sandbox_of)} /> : null}
        <UserMenu />
      </div>
    </nav>
  );
}

/** The workspace at the foot of the rail: its initial; opens the switcher and the test-mode toggle. */
function WorkspaceButton({ name, sandbox }: { name: string; sandbox: boolean }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close); document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open]);
  return (
    <div className="ir-ws" ref={box}>
      <button type="button" className={`ir-ws-btn${sandbox ? ' sandbox' : ''}`} aria-haspopup="dialog" aria-expanded={open} title={`${name}${sandbox ? ' (test mode)' : ''}`} aria-label={`Workspace: ${name}${sandbox ? ', test mode' : ''}`} onClick={() => setOpen(!open)}>
        {name.replace(/\s*\(sandbox\)$/i, '').slice(0, 1).toUpperCase()}
      </button>
      {open ? <div className="ir-ws-pop" role="dialog" aria-label="Workspace"><WorkspaceSwitcher inline onDone={() => setOpen(false)} /><TestModeToggle /></div> : null}
    </div>
  );
}
